import type { RoomConfig, RoomPoint, WallSegmentConfig } from '../types';

export function makeRoomPolygon(sides: number, width: number, depth: number): RoomPoint[] {
  if (sides === 4) {
    return [
      { x: -width / 2, z: depth / 2 },
      { x: -width / 2, z: -depth / 2 },
      { x: width / 2, z: -depth / 2 },
      { x: width / 2, z: depth / 2 },
    ];
  }
  return Array.from({ length: sides }, (_, index) => {
    const angle = Math.PI / 2 + (index / sides) * Math.PI * 2;
    return {
      x: Number((Math.cos(angle) * width / 2).toFixed(2)),
      z: Number((Math.sin(angle) * depth / 2).toFixed(2)),
    };
  });
}

export function getRoomPolygon(room: RoomConfig): RoomPoint[] {
  const sides = Math.min(7, Math.max(4, room.shapeSides ?? room.polygon?.length ?? 4));
  if (room.polygon?.length === sides) return room.polygon;
  return makeRoomPolygon(sides, room.width, room.depth);
}

const DEFAULT_WALL_DECORATION = {
  type: 'none' as const,
  color: '#e07a3a',
  height: 0.35,
  offsetY: 1.35,
};

function resolvePillarSpacing(wall: WallSegmentConfig): WallSegmentConfig['pillarSpacing'] {
  if (wall.pillarSpacing === 0 || wall.pillarSpacing === 1 || wall.pillarSpacing === 2 || wall.pillarSpacing === 3) {
    return wall.pillarSpacing;
  }
  if (wall.pillarsEvery1m === false) return 0;
  return 1;
}

function normalizeWallSegment(wall: WallSegmentConfig): WallSegmentConfig {
  return {
    ...wall,
    pillarSpacing: resolvePillarSpacing(wall),
    decoration: wall.decoration ?? DEFAULT_WALL_DECORATION,
  };
}

function legacyWall(room: RoomConfig, index: number): WallSegmentConfig {
  if (index === 0) {
    return normalizeWallSegment({
      enabled: room.wallWest,
      style: 'solid',
      material: room.wallWestMaterial ?? 'white-concrete',
      imageUrl: room.wallWestImageUrl,
      imageName: room.wallWestImageName,
    });
  }
  if (index === 1) {
    return normalizeWallSegment({
      enabled: room.wallNorth,
      style: 'solid',
      material: room.wallNorthMaterial ?? 'white-concrete',
      imageUrl: room.wallNorthImageUrl,
      imageName: room.wallNorthImageName,
    });
  }
  return normalizeWallSegment({ enabled: false, style: 'solid', material: 'white-concrete' });
}

export function getRoomWalls(room: RoomConfig): WallSegmentConfig[] {
  const count = getRoomPolygon(room).length - 1;
  return Array.from({ length: count }, (_, index) => normalizeWallSegment(room.wallSegments?.[index] ?? legacyWall(room, index)));
}

export const DEFAULT_FLOOR_BLANKET = {
  enabled: true,
  material: 'gray-felt' as const,
  color: '#8a9090',
  style: 'plain' as const,
};

export function getFloorBlanket(room: RoomConfig) {
  return { ...DEFAULT_FLOOR_BLANKET, ...room.floorBlanket };
}

export function roomBounds(points: RoomPoint[]) {
  const xs = points.map((point) => point.x);
  const zs = points.map((point) => point.z);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minZ = Math.min(...zs);
  const maxZ = Math.max(...zs);
  return { minX, maxX, minZ, maxZ, width: maxX - minX, depth: maxZ - minZ };
}

export function pointInPolygon(x: number, z: number, polygon: RoomPoint[]) {
  let inside = false;
  for (let index = 0, previous = polygon.length - 1; index < polygon.length; previous = index++) {
    const a = polygon[index];
    const b = polygon[previous];
    const intersects = ((a.z > z) !== (b.z > z))
      && (x < ((b.x - a.x) * (z - a.z)) / ((b.z - a.z) || Number.EPSILON) + a.x);
    if (intersects) inside = !inside;
  }
  return inside;
}
